// [claude] MCP marketing setup guide. Client picker is a logo+button grid
// (car-screen tap targets) that drives per-client install steps. Server URL /
// OAuth copy is shared. Branded marks are CSS-masked silhouettes painted with
// currentColor so they inherit the button text (light on dark). Other Clients
// uses an inline apps glyph with the same currentColor. See SOURCES.md.
// Last updated: 2026-09-23
import React, { useState } from 'react';
import styled from 'styled-components';
import ScrollReveal from './shared/ScrollReveal';

const Section = styled.section`
  padding: 96px 24px;
  max-width: 1120px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 64px 16px;
  }
`;

const Header = styled.div`
  margin-bottom: 48px;

  @media (max-width: 768px) {
    margin-bottom: 36px;
  }
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.accent};
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 600;
  color: ${p => p.theme.text};
  letter-spacing: -0.03em;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

/* ---- Compact 3-step overview ---- */

const StepsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 48px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 36px;
  }
`;

const StepCard = styled.div`
  padding: 20px;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
`;

const StepNum = styled.div`
  font-family: ${p => p.theme.fontMono};
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  margin-bottom: 8px;
`;

const StepTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${p => p.theme.text};
  margin-bottom: 6px;
`;

const StepDesc = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${p => p.theme.textSecondary};
`;

/* ---- Server URL ---- */

const ServerUrl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
  padding: 14px 18px;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

const UrlLabel = styled.span`
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  white-space: nowrap;
`;

const UrlValue = styled.code`
  font-family: ${p => p.theme.fontMono};
  font-size: 14px;
  color: ${p => p.theme.accent};
  flex: 1;
  /* the pages layout ships Bootstrap, which skins bare <code> pink */
  background: transparent;
  padding: 0;
`;

const CopyButton = styled.button`
  background: none;
  border: 1px solid ${p => p.theme.border};
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: ${p => p.copied ? p.theme.green : p.theme.textTertiary};
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  font-family: ${p => p.theme.fontFamily};
  min-width: 56px;

  &:hover {
    color: ${p => p.copied ? p.theme.green : p.theme.textSecondary};
    border-color: ${p => p.theme.borderHover};
  }
`;

/* ---- Client picker ---- */

const PickerLabel = styled.div`
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  margin-bottom: 12px;
`;

const Picker = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const PickerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 64px;
  padding: 14px 16px;
  background: ${p => p.active ? p.theme.accentDim : p.theme.surface};
  border: 1px solid ${p => p.active ? p.theme.accent : p.theme.border};
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  font-family: ${p => p.theme.fontFamily};
  color: ${p => p.theme.text};
  cursor: pointer;
  text-align: left;
  line-height: 1.25;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${p => p.active ? p.theme.accent : p.theme.borderHover};
  }

  &:focus-visible {
    outline: 2px solid ${p => p.theme.accent};
    outline-offset: 2px;
  }
`;

const LogoMark = styled.span`
  display: block;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: inherit;
  background-color: currentColor;
  -webkit-mask-image: url("${p => p.$src}");
  mask-image: url("${p => p.$src}");
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
`;

const AppsMark = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: ${p => p.theme.text};
`;

const TabContent = styled.div``;

/* ---- Install button ---- */

const InstallButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 24px;
  background: ${p => p.theme.accent};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: ${p => p.theme.fontFamily};
  border-radius: 10px;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease;
  margin-bottom: 20px;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    opacity: 0.85;
    transform: translateY(0);
  }
`;

const InstallNote = styled.div`
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  margin-bottom: 20px;
`;

/* ---- CLI Block ---- */

const CliBlock = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
  overflow: hidden;
  max-width: 620px;
`;

const CliBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid ${p => p.theme.border};
`;

const CliDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--tz-hairline, rgba(255, 255, 255, 0.08));
`;

const CliBarTitle = styled.span`
  font-family: ${p => p.theme.fontMono};
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  margin-left: 4px;
`;

const CliCode = styled.pre`
  font-family: ${p => p.theme.fontMono};
  font-size: 13px;
  line-height: 1.8;
  color: ${p => p.theme.textSecondary};
  padding: 18px 20px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  /* the pages layout ships Bootstrap, which skins bare <pre> light grey */
  background: transparent;
  border: 0;
`;

const CliPrompt = styled.span`color: ${p => p.theme.accent};`;
const CliCmd = styled.span`color: ${p => p.theme.text};`;
const CliFlag = styled.span`color: ${p => p.theme.textTertiary};`;
const CliUrl = styled.span`color: #7ee787;`;
const CliComment = styled.span`color: ${p => p.theme.textTertiary};`;

/* ---- Generic instructions ---- */

const InstructionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 520px;
`;

const InstructionStep = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 14px;
`;

const InstrNum = styled.div`
  font-family: ${p => p.theme.fontMono};
  font-size: 13px;
  color: ${p => p.theme.textTertiary};
  padding-top: 1px;
`;

const InstrText = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: ${p => p.theme.textSecondary};
`;

const InlineCode = styled.code`
  font-family: ${p => p.theme.fontMono};
  font-size: 13px;
  color: ${p => p.theme.accent};
  background: ${p => p.theme.accentDim};
  padding: 2px 6px;
  border-radius: 4px;
`;

/* ---- Data ---- */

const STEPS = [
  {
    num: '01',
    title: 'Connect',
    desc: 'Add TezLab as an MCP server in your AI client. Authorize via OAuth.',
  },
  {
    num: '02',
    title: 'Ask',
    desc: 'Ask anything — charging costs, battery health, driving patterns, or even "where\'s my happy place?"',
  },
  {
    num: '03',
    title: 'Act',
    desc: 'Send commands to your Tesla with confirmation. Read data for any vehicle.',
  },
];

var CLIENT_ICON = {
  claude: '/images/mcp-clients/claude.svg',
  chatgpt: '/images/mcp-clients/chatgpt.svg',
  grok: '/images/mcp-clients/grok.svg',
  cursor: '/images/mcp-clients/cursor.svg',
  vscode: '/images/mcp-clients/vscode.svg',
};

var CLIENTS = [
  { key: 'claude', label: 'Claude Desktop', icon: CLIENT_ICON.claude },
  { key: 'cli', label: 'Claude Code', icon: CLIENT_ICON.claude },
  { key: 'chatgpt', label: 'ChatGPT', icon: CLIENT_ICON.chatgpt },
  { key: 'grok', label: 'Grok', icon: CLIENT_ICON.grok },
  { key: 'cursor', label: 'Cursor', icon: CLIENT_ICON.cursor },
  { key: 'vscode', label: 'VS Code', icon: CLIENT_ICON.vscode },
  { key: 'other', label: 'Other Clients' },
];

function AppsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
    </svg>
  );
}

function ClientMark(props) {
  var client = props.client;
  if (client.icon) {
    return <LogoMark $src={client.icon} aria-hidden="true" />;
  }
  return (
    <AppsMark>
      <AppsIcon />
    </AppsMark>
  );
}

var MCP_URL = 'https://mcp.tezlabapp.com';

var CURSOR_DEEP_LINK = 'cursor://anysphere.cursor-deeplink/mcp/install?name=tezlab&config=' +
  btoa(JSON.stringify({ type: 'http', url: MCP_URL }));

var VSCODE_DEEP_LINK = 'vscode:mcp/install?' +
  encodeURIComponent(JSON.stringify({ name: 'tezlab', type: 'http', url: MCP_URL }));

export default function SetupGuide() {
  var clientResult = useState(CLIENTS[0].key);
  var activeClient = clientResult[0];
  var setActiveClient = clientResult[1];
  var copyResult = useState(false);
  var copied = copyResult[0];
  var setCopied = copyResult[1];

  function handleCopy() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(MCP_URL).then(function() {
        setCopied(true);
        setTimeout(function() { setCopied(false); }, 2000);
      });
    }
  }

  return (
    <Section>
      <ScrollReveal>
        <Header>
          <SectionLabel>How it works</SectionLabel>
          <SectionTitle>Three steps, under a minute</SectionTitle>
        </Header>
      </ScrollReveal>

      <ScrollReveal>
        <StepsRow>
          {STEPS.map(function(s) {
            return (
              <StepCard key={s.num}>
                <StepNum>{s.num}</StepNum>
                <StepTitle>{s.title}</StepTitle>
                <StepDesc>{s.desc}</StepDesc>
              </StepCard>
            );
          })}
        </StepsRow>
      </ScrollReveal>

      <ScrollReveal>
        <ServerUrl>
          <UrlLabel>Server URL</UrlLabel>
          <UrlValue>{MCP_URL}</UrlValue>
          <CopyButton onClick={handleCopy} copied={copied}>
            {copied ? 'Copied' : 'Copy'}
          </CopyButton>
        </ServerUrl>

        <PickerLabel>Choose a client</PickerLabel>
        <Picker role="radiogroup" aria-label="MCP client">
          {CLIENTS.map(function(c) {
            var active = activeClient === c.key;
            return (
              <PickerButton
                key={c.key}
                type="button"
                role="radio"
                aria-label={c.label}
                aria-checked={active}
                active={active}
                onClick={function() { setActiveClient(c.key); }}
              >
                <ClientMark client={c} />
                {c.label}
              </PickerButton>
            );
          })}
        </Picker>

        {activeClient === 'claude' && (
          <TabContent>
            <InstructionList>
              <InstructionStep>
                <InstrNum>1.</InstrNum>
                <InstrText>
                  Open Claude Desktop and go to <InlineCode>Settings</InlineCode> &rarr; <InlineCode>Integrations</InlineCode>.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>2.</InstrNum>
                <InstrText>
                  Click <InlineCode>Add custom connector</InlineCode> and enter <InlineCode>{MCP_URL}</InlineCode> as the URL.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>3.</InstrNum>
                <InstrText>
                  Claude will open a TezLab OAuth window. Sign in with your TezLab account to authorize access.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>4.</InstrNum>
                <InstrText>
                  Once connected, TezLab tools appear in your conversation. Start by asking about your vehicles.
                </InstrText>
              </InstructionStep>
            </InstructionList>
          </TabContent>
        )}

        {activeClient === 'cli' && (
          <TabContent>
            <CliBlock>
              <CliBar>
                <CliDot /><CliDot /><CliDot />
                <CliBarTitle>Terminal</CliBarTitle>
              </CliBar>
              <CliCode>
<CliComment># Add TezLab MCP server</CliComment>{'\n'}
<CliPrompt>$ </CliPrompt><CliCmd>claude mcp add</CliCmd> <CliFlag>--transport http</CliFlag> <CliCmd>tezlab</CliCmd> <CliUrl>{MCP_URL}</CliUrl>{'\n'}
{'\n'}
<CliComment># Open Claude Code, then authorize</CliComment>{'\n'}
<CliPrompt>$ </CliPrompt><CliCmd>claude</CliCmd>{'\n'}
<CliPrompt>&gt; </CliPrompt><CliCmd>/mcp</CliCmd>{'\n'}
<CliComment># Select tezlab → opens browser to authorize</CliComment>
              </CliCode>
            </CliBlock>
          </TabContent>
        )}

        {activeClient === 'chatgpt' && (
          <TabContent>
            <InstructionList>
              <InstructionStep>
                <InstrNum>1.</InstrNum>
                <InstrText>
                  Open ChatGPT, go to <InlineCode>Settings</InlineCode> &rarr; <InlineCode>Connected apps</InlineCode> and enable Developer Mode under Advanced.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>2.</InstrNum>
                <InstrText>
                  Click <InlineCode>Add connector</InlineCode> and enter <InlineCode>{MCP_URL}</InlineCode> as the server URL.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>3.</InstrNum>
                <InstrText>
                  ChatGPT will open a TezLab OAuth window. Sign in with your TezLab account to authorize access.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>4.</InstrNum>
                <InstrText>
                  Once connected, TezLab tools appear in the chat. Requires ChatGPT Plus or Pro.
                </InstrText>
              </InstructionStep>
            </InstructionList>
          </TabContent>
        )}

        {activeClient === 'grok' && (
          <TabContent>
            <InstallButton href="https://grok.com/connectors">
              Open Grok Connectors &rarr;
            </InstallButton>
            <InstallNote>
              Opens <InlineCode>grok.com/connectors</InlineCode>. Custom MCP connectors are available to all Grok users.
            </InstallNote>
            <InstructionList>
              <InstructionStep>
                <InstrNum>1.</InstrNum>
                <InstrText>
                  Go to <InlineCode>grok.com/connectors</InlineCode>.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>2.</InstrNum>
                <InstrText>
                  Click <InlineCode>New Connector</InlineCode>, then select <InlineCode>Custom</InlineCode>.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>3.</InstrNum>
                <InstrText>
                  Enter <InlineCode>{MCP_URL}</InlineCode> as the MCP server URL.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>4.</InstrNum>
                <InstrText>
                  Complete the TezLab OAuth prompt. Sign in with your TezLab account to authorize access.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>5.</InstrNum>
                <InstrText>
                  Once connected, Grok discovers TezLab tools and makes them available in conversation.
                  On Grok Business and Enterprise, a team admin must provision the connector first.
                </InstrText>
              </InstructionStep>
            </InstructionList>
          </TabContent>
        )}

        {activeClient === 'cursor' && (
          <TabContent>
            <InstallButton href={CURSOR_DEEP_LINK}>
              Add to Cursor &rarr;
            </InstallButton>
            <InstallNote>Opens Cursor and adds TezLab automatically. You'll authorize via OAuth on first use.</InstallNote>
            <InstructionList>
              <InstructionStep>
                <InstrNum>or</InstrNum>
                <InstrText>
                  Open Cursor Settings &rarr; <InlineCode>MCP</InlineCode> &rarr; <InlineCode>Add new MCP server</InlineCode>. Set type to <InlineCode>HTTP</InlineCode> and URL to <InlineCode>{MCP_URL}</InlineCode>.
                </InstrText>
              </InstructionStep>
            </InstructionList>
          </TabContent>
        )}

        {activeClient === 'vscode' && (
          <TabContent>
            <InstallButton href={VSCODE_DEEP_LINK}>
              Add to VS Code &rarr;
            </InstallButton>
            <InstallNote>Opens VS Code and adds TezLab as an MCP server for GitHub Copilot. Authorize via OAuth on first use.</InstallNote>
            <InstructionList>
              <InstructionStep>
                <InstrNum>or</InstrNum>
                <InstrText>
                  Open the Command Palette &rarr; <InlineCode>MCP: Add Server</InlineCode>. Choose <InlineCode>HTTP</InlineCode> and enter <InlineCode>{MCP_URL}</InlineCode>.
                </InstrText>
              </InstructionStep>
            </InstructionList>
          </TabContent>
        )}

        {activeClient === 'other' && (
          <TabContent>
            <InstructionList>
              <InstructionStep>
                <InstrNum>1.</InstrNum>
                <InstrText>
                  Open your MCP client's settings and find the option to add a new MCP server or connector.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>2.</InstrNum>
                <InstrText>
                  Enter <InlineCode>{MCP_URL}</InlineCode> as the server URL. The transport type is <InlineCode>Streamable HTTP</InlineCode>.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>3.</InstrNum>
                <InstrText>
                  Your client will open a TezLab OAuth window. Sign in with your TezLab account to authorize access.
                </InstrText>
              </InstructionStep>
              <InstructionStep>
                <InstrNum>4.</InstrNum>
                <InstrText>
                  Once connected, you'll see TezLab's tools available in your client. Start by asking about your vehicles.
                </InstrText>
              </InstructionStep>
            </InstructionList>
          </TabContent>
        )}
      </ScrollReveal>
    </Section>
  );
}
